import { eventBus, EventTypes } from './EventBus';
import { workflowEngine } from './WorkflowEngine';
import { unifiedSearchService } from './UnifiedSearch';

export interface MetricValue {
  value: number;
  timestamp: number;
  labels: Record<string, string>;
}

export interface Metric {
  name: string;
  type: 'counter' | 'gauge' | 'histogram' | 'summary';
  help: string;
  values: MetricValue[];
}

export interface MetricsSnapshot {
  timestamp: number;
  metrics: Metric[];
}

export class MetricsService {
  private static instance: MetricsService;
  private metrics: Map<string, Metric> = new Map();
  private startTime: number = Date.now();

  private constructor() {
    this.initializeMetrics();
    this.setupEventListeners();
  }

  static getInstance(): MetricsService {
    if (!MetricsService.instance) {
      MetricsService.instance = new MetricsService();
    }
    return MetricsService.instance;
  }

  private initializeMetrics(): void {
    // Event Bus Metrics
    this.createMetric('events_published_total', 'counter', 'Total number of events published');
    this.createMetric('events_failed_total', 'counter', 'Total number of events that failed processing');
    this.createMetric('events_processing_duration_seconds', 'histogram', 'Event processing duration in seconds');
    this.createMetric('active_subscribers', 'gauge', 'Number of active event subscribers');
    this.createMetric('dlq_size', 'gauge', 'Number of events in dead letter queue');

    // Workflow Engine Metrics
    this.createMetric('workflows_executed_total', 'counter', 'Total number of workflows executed');
    this.createMetric('workflow_execution_latency_ms', 'histogram', 'Workflow execution latency in milliseconds');
    this.createMetric('workflows_failed_total', 'counter', 'Total number of workflows that failed');
    this.createMetric('active_workflow_instances', 'gauge', 'Number of active workflow instances');
    this.createMetric('workflow_steps_completed_total', 'counter', 'Total number of workflow steps completed');

    // Search Service Metrics
    this.createMetric('search_queries_total', 'counter', 'Total number of search queries');
    this.createMetric('search_query_duration_ms', 'histogram', 'Search query duration in milliseconds');
    this.createMetric('search_index_size', 'gauge', 'Number of items in search index');
    this.createMetric('search_index_updates_total', 'counter', 'Total number of search index updates');

    // System Health Metrics
    this.createMetric('system_uptime_seconds', 'gauge', 'System uptime in seconds');
    this.createMetric('memory_usage_bytes', 'gauge', 'Memory usage in bytes');
    this.createMetric('cpu_usage_percent', 'gauge', 'CPU usage percentage');
    this.createMetric('api_requests_total', 'counter', 'Total number of API requests');
    this.createMetric('api_request_duration_ms', 'histogram', 'API request duration in milliseconds');

    // Integration Metrics
    this.createMetric('integration_sync_operations_total', 'counter', 'Total number of integration sync operations');
    this.createMetric('integration_sync_duration_ms', 'histogram', 'Integration sync duration in milliseconds');
    this.createMetric('integration_errors_total', 'counter', 'Total number of integration errors');
    this.createMetric('data_validation_errors_total', 'counter', 'Total number of data validation errors');
  }

  private setupEventListeners(): void {
    // Listen for event bus metrics
    eventBus.subscribe('*', (payload) => {
      this.incrementCounter('events_published_total', { event_type: payload.eventType, source: payload.source });
    });

    // Listen for workflow events
    eventBus.subscribe(EventTypes.WORKFLOW_TRIGGER, (payload) => {
      this.incrementCounter('workflows_executed_total', { 
        workflow_id: payload.data.workflowId || 'unknown',
        status: payload.data.status || 'unknown'
      });
    });

    // Listen for search events
    eventBus.subscribe(EventTypes.USER_ACTIVITY, (payload) => {
      if (payload.source === 'unified-search') {
        this.incrementCounter('search_queries_total', { 
          query_type: payload.data.query ? 'search' : 'suggestion'
        });
      }
    });

    // Update system metrics periodically
    setInterval(() => {
      this.updateSystemMetrics();
    }, 30000); // Every 30 seconds
  }

  private createMetric(name: string, type: Metric['type'], help: string): void {
    this.metrics.set(name, {
      name,
      type,
      help,
      values: []
    });
  }

  // Increment a counter metric
  incrementCounter(name: string, labels: Record<string, string> = {}): void {
    const metric = this.metrics.get(name);
    if (metric && metric.type === 'counter') {
      const existingValue = metric.values.find(v => 
        Object.entries(v.labels).every(([k, v]) => labels[k] === v) &&
        Object.entries(labels).every(([k, v]) => v.labels[k] === v)
      );

      if (existingValue) {
        existingValue.value++;
        existingValue.timestamp = Date.now();
      } else {
        metric.values.push({
          value: 1,
          timestamp: Date.now(),
          labels
        });
      }
    }
  }

  // Set a gauge metric
  setGauge(name: string, value: number, labels: Record<string, string> = {}): void {
    const metric = this.metrics.get(name);
    if (metric && metric.type === 'gauge') {
      const existingValue = metric.values.find(v => 
        Object.entries(v.labels).every(([k, v]) => labels[k] === v) &&
        Object.entries(labels).every(([k, v]) => v.labels[k] === v)
      );

      if (existingValue) {
        existingValue.value = value;
        existingValue.timestamp = Date.now();
      } else {
        metric.values.push({
          value,
          timestamp: Date.now(),
          labels
        });
      }
    }
  }

  // Record a histogram metric
  recordHistogram(name: string, value: number, labels: Record<string, string> = {}): void {
    const metric = this.metrics.get(name);
    if (metric && metric.type === 'histogram') {
      metric.values.push({
        value,
        timestamp: Date.now(),
        labels
      });

      // Keep only last 1000 values for histograms
      if (metric.values.length > 1000) {
        metric.values = metric.values.slice(-1000);
      }
    }
  }

  private updateSystemMetrics(): void {
    // Update system uptime
    const uptime = (Date.now() - this.startTime) / 1000;
    this.setGauge('system_uptime_seconds', uptime);

    // Update memory usage (simulated)
    const memoryUsage = process.memoryUsage?.() || { heapUsed: 0, heapTotal: 0 };
    this.setGauge('memory_usage_bytes', memoryUsage.heapUsed || 0);

    // Update active subscribers
    const subscriberStats = eventBus.getSubscriptionStats();
    const totalSubscribers = Object.values(subscriberStats).reduce((sum, count) => sum + count, 0);
    this.setGauge('active_subscribers', totalSubscribers);

    // Update DLQ size
    const dlqSize = eventBus.getDeadLetterQueue().length;
    this.setGauge('dlq_size', dlqSize);

    // Update active workflow instances
    const activeInstances = workflowEngine.getAllInstances().filter(i => i.status === 'running').length;
    this.setGauge('active_workflow_instances', activeInstances);

    // Update search index size
    const searchStats = unifiedSearchService.getSearchStats();
    const totalIndexed = Object.values(searchStats).reduce((sum, count) => sum + count, 0);
    this.setGauge('search_index_size', totalIndexed);
  }

  // Get metrics in Prometheus format
  getPrometheusMetrics(): string {
    let output = '';

    for (const metric of this.metrics.values()) {
      // Add help text
      output += `# HELP ${metric.name} ${metric.help}\n`;
      output += `# TYPE ${metric.name} ${metric.type}\n`;

      // Add metric values
      for (const value of metric.values) {
        const labels = Object.entries(value.labels)
          .map(([k, v]) => `${k}="${v}"`)
          .join(',');

        const labelString = labels ? `{${labels}}` : '';
        output += `${metric.name}${labelString} ${value.value}\n`;
      }

      output += '\n';
    }

    return output;
  }

  // Get metrics snapshot
  getMetricsSnapshot(): MetricsSnapshot {
    return {
      timestamp: Date.now(),
      metrics: Array.from(this.metrics.values())
    };
  }

  // Get specific metric
  getMetric(name: string): Metric | undefined {
    return this.metrics.get(name);
  }

  // Reset all metrics
  resetMetrics(): void {
    this.metrics.clear();
    this.initializeMetrics();
    console.log('[MetricsService] All metrics reset');
  }

  // Get metrics summary for dashboard
  getMetricsSummary(): Record<string, any> {
    const summary: Record<string, any> = {};

    // Event metrics
    const eventsPublished = this.getMetric('events_published_total');
    const eventsFailed = this.getMetric('events_failed_total');
    const activeSubscribers = this.getMetric('active_subscribers');

    summary.events = {
      published: eventsPublished?.values.reduce((sum, v) => sum + v.value, 0) || 0,
      failed: eventsFailed?.values.reduce((sum, v) => sum + v.value, 0) || 0,
      activeSubscribers: activeSubscribers?.values[0]?.value || 0
    };

    // Workflow metrics
    const workflowsExecuted = this.getMetric('workflows_executed_total');
    const workflowsFailed = this.getMetric('workflows_failed_total');
    const activeWorkflows = this.getMetric('active_workflow_instances');

    summary.workflows = {
      executed: workflowsExecuted?.values.reduce((sum, v) => sum + v.value, 0) || 0,
      failed: workflowsFailed?.values.reduce((sum, v) => sum + v.value, 0) || 0,
      active: activeWorkflows?.values[0]?.value || 0
    };

    // Search metrics
    const searchQueries = this.getMetric('search_queries_total');
    const searchIndexSize = this.getMetric('search_index_size');

    summary.search = {
      queries: searchQueries?.values.reduce((sum, v) => sum + v.value, 0) || 0,
      indexSize: searchIndexSize?.values[0]?.value || 0
    };

    // System metrics
    const uptime = this.getMetric('system_uptime_seconds');
    const memoryUsage = this.getMetric('memory_usage_bytes');
    const dlqSize = this.getMetric('dlq_size');

    summary.system = {
      uptime: uptime?.values[0]?.value || 0,
      memoryUsage: memoryUsage?.values[0]?.value || 0,
      dlqSize: dlqSize?.values[0]?.value || 0
    };

    return summary;
  }

  // Record API request metrics
  recordApiRequest(method: string, path: string, statusCode: number, duration: number): void {
    this.incrementCounter('api_requests_total', { 
      method, 
      path, 
      status_code: statusCode.toString() 
    });
    
    this.recordHistogram('api_request_duration_ms', duration, { 
      method, 
      path 
    });
  }

  // Record integration sync metrics
  recordIntegrationSync(operation: string, duration: number, success: boolean): void {
    this.incrementCounter('integration_sync_operations_total', { 
      operation, 
      success: success.toString() 
    });
    
    this.recordHistogram('integration_sync_duration_ms', duration, { 
      operation 
    });

    if (!success) {
      this.incrementCounter('integration_errors_total', { operation });
    }
  }

  // Record data validation errors
  recordValidationError(schema: string, error: string): void {
    this.incrementCounter('data_validation_errors_total', { 
      schema, 
      error_type: error 
    });
  }

  // Record workflow execution metrics
  recordWorkflowExecution(workflowId: string, duration: number, success: boolean): void {
    this.recordHistogram('workflow_execution_latency_ms', duration, { 
      workflow_id: workflowId 
    });

    if (!success) {
      this.incrementCounter('workflows_failed_total', { 
        workflow_id: workflowId 
      });
    }
  }

  // Record search query metrics
  recordSearchQuery(query: string, duration: number, resultCount: number): void {
    this.recordHistogram('search_query_duration_ms', duration, { 
      query_type: query.length > 50 ? 'long' : 'short' 
    });
  }
}

// Export singleton instance
export const metricsService = MetricsService.getInstance();

// Export types
export type { Metric, MetricValue, MetricsSnapshot };